import { useState } from "react";
import {
  addUser as addUserService,
  type AddUserParameters,
} from "@/services/auth";
import { t } from "i18next";
import { AUTH_ERRORS } from "@/common/errors";
import type { AxiosError } from "axios";
import type { ApiErrorResponse } from "@/lib/axios/axiosClient";

type CreateUserParameters = Omit<AddUserParameters, "fUid"> & {
  password: string;
  turnstileToken?: string;
};

interface UseCreateUserState {
  createUser: (body: CreateUserParameters) => Promise<void>;
  result: boolean;
  loading: boolean;
  error: string | null;
}

export const useCreateUser = (): UseCreateUserState => {
  const [result, setResult] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const createUser = async (body: CreateUserParameters): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await addUserService({
        email: body.email,
        firstname: body.firstname,
        lastname: body.lastname,
        password: body.password,
        occupationId: body.occupationId,
        turnstileToken: body.turnstileToken,
      });
      setResult(true);
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (
        axiosError.response?.data.error.code == AUTH_ERRORS.EMAIL_ALREADY_IN_USE
      ) {
        setError(t("signUp.form.messages.emailUsed"));
      } else {
        setError(t("signUp.form.messages.error"));
      }
      setResult(false);
    } finally {
      setLoading(false);
    }
  };
  return { createUser, result, loading, error };
};
